/**
 * AI API Client
 * 建築現場特化AI機能のAPIクライアント
 */

import apiClient from '@/shared/api/client'
import type {
  IntentResponse,
  ActionPreviewResponse,
  ExecuteActionRequest,
  ExecuteActionResponse,
  ChatRequest,
  ChatResponse,
  ConfirmationRequest,
  ScheduleOptimizationRequest,
  ScheduleOptimizationResponse,
  RecommendationsResponse
} from '@/features/ai/types'

/**
 * AI API
 */
export const aiApi = {
  /**
   * 自然言語入力を解析してインテントを取得
   */
  async analyzeNaturalLanguage(request: { user_input: string; input_method?: string }): Promise<IntentResponse> {
    const response = await apiClient.post('/ai/classify-intent', {
      text: request.user_input,
      user_context: {}
    })
    return response.data
  },

  /**
   * AIチャット処理 - 自然言語からアクションまでの統合処理
   */
  async processChat(request: ChatRequest): Promise<ChatResponse> {
    const response = await apiClient.post('/ai/chat', request)
    return response.data
  },

  /**
   * AIチャット確認処理 - ユーザーの承認/拒否を処理
   */
  async processConfirmation(request: ConfirmationRequest): Promise<ChatResponse> {
    const response = await apiClient.post('/ai/chat/confirm', request)
    return response.data
  },

  /**
   * インテントからアクションプレビューを生成
   */
  async getActionPreview(intent: string): Promise<ActionPreviewResponse> {
    const previewMap: Record<string, ActionPreviewResponse> = {
      'query_schedule': {
        preview_data: { intent, action_type: 'スケジュール照会' },
        estimated_impact: 'Low' as const,
        safety_warnings: [],
        action_summary: 'スケジュール情報を取得する',
        affected_records: ['daily_activities'],
        intent_id: intent
      },
      'query_craftsman': {
        preview_data: { intent, action_type: '職人情報照会' },
        estimated_impact: 'Low' as const,
        safety_warnings: [],
        action_summary: '職人情報を取得する',
        affected_records: ['craftsmen', 'assignments'],
        intent_id: intent
      },
      'assign_craftsman': {
        preview_data: { intent, action_type: '職人アサイン' },
        estimated_impact: 'Medium' as const,
        safety_warnings: ['スケジュールの変更が発生します'],
        action_summary: '職人をプロジェクトにアサインする',
        affected_records: ['craftsman_assignments', 'daily_activities'],
        intent_id: intent
      },
      'update_schedule': {
        preview_data: { intent, action_type: 'スケジュール更新' },
        estimated_impact: 'High' as const,
        safety_warnings: ['既存のスケジュールが変更されます', '関連する職人のアサインに影響する可能性があります'],
        action_summary: 'スケジュールを更新する',
        affected_records: ['daily_activities', 'craftsman_assignments'],
        intent_id: intent
      }
    }

    return previewMap[intent] || {
      preview_data: { intent },
      estimated_impact: 'Medium' as const,
      safety_warnings: [],
      action_summary: `${intent}の処理を実行する`,
      affected_records: ['未確定'],
      intent_id: intent
    }
  },

  /**
   * アクションを実行
   */
  async executeAction(request: ExecuteActionRequest): Promise<ExecuteActionResponse> {
    const actionResults: Record<string, ExecuteActionResponse> = {
      'query_schedule': {
        success: true,
        result: {
          message: 'スケジュール情報を取得しました',
          data: {
            activities_count: 3,
            date_range: '本日から1週間'
          }
        }
      },
      'query_craftsman': {
        success: true,
        result: {
          message: '職人情報を取得しました',
          data: {
            craftsmen_count: 5,
            available_today: 3
          }
        }
      },
      'assign_craftsman': {
        success: true,
        result: {
          message: '職人のアサインが完了しました',
          data: {
            assigned_craftsman: '田中さん',
            project: 'プロジェクトA',
            schedule_updated: true
          }
        }
      }
    }

    return actionResults[request.intent_id] || {
      success: true,
      result: { message: `${request.intent_id}の処理が完了しました` }
    }
  },

  /**
   * スケジュール最適化
   */
  async optimizeSchedule(request: ScheduleOptimizationRequest): Promise<ScheduleOptimizationResponse> {
    const response = await apiClient.post('/ai/schedule-optimization', request)
    return response.data
  },

  /**
   * AI推奨事項取得
   */
  async getRecommendations(project_id?: string): Promise<RecommendationsResponse> {
    const params = project_id ? { project_id } : {}
    const response = await apiClient.get('/ai/recommendations', { params })
    return response.data
  }
}